import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { moduleEvaluationsAPI } from '../../services/api';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { exportToExcel, exportToPDF } from '../../utils/exportUtils';
import './ModuleEvaluations.css';

const SCORE_OPTIONS = [
  { value: 'helemaal_oneens', label: 'Helemaal oneens' },
  { value: 'oneens', label: 'Oneens' },
  { value: 'eens', label: 'Eens' },
  { value: 'helemaal_eens', label: 'Helemaal eens' },
  { value: 'niet_van_toepassing', label: 'Niet van toepassing' }
];

const DEFAULT_SEGMENTS = [
  { key: 'stimulerend_onderwijs', title: 'Segment 1. Stimulerend Onderwijs', sortOrder: 1, questions: [] },
  { key: 'rol_van_docent', title: 'Segment 2. Rol van de Docent', sortOrder: 2, questions: [] },
  { key: 'curriculum_aspecten', title: 'Segment 3. Curriculum aspecten', sortOrder: 3, questions: [] },
  { key: 'faciliteiten', title: 'Segment 4. Faciliteiten', sortOrder: 4, questions: [] }
];

const buildTemplateForm = (template) => {
  if (!template) {
    return {
      name: '',
      description: '',
      moduleId: '',
      location: 'all',
      isActive: true,
      segments: DEFAULT_SEGMENTS.map((segment) => ({ ...segment, questions: [] }))
    };
  }

  return {
    name: template.name || '',
    description: template.description || '',
    moduleId: template.module?.id || template.module?._id || template.module || '',
    location: template.location || 'all',
    isActive: template.isActive !== false,
    segments: (template.segments || []).map((segment, segmentIndex) => ({
      key: segment.key || `segment_${segmentIndex + 1}`,
      title: segment.title || `Segment ${segmentIndex + 1}`,
      sortOrder: Number(segment.sortOrder || segmentIndex + 1),
      questions: (segment.questions || []).map((question, questionIndex) => ({
        id: question._id || question.id || `${segment.key}-${questionIndex}`,
        text: question.text || '',
        sortOrder: Number(question.sortOrder || questionIndex),
        isActive: question.isActive !== false
      }))
    }))
  };
};

const getIdString = (value) => String(value?.id || value?._id || value || '');

const ModuleEvaluations = () => {
  const { user } = useAuth();
  const { data } = useData();
  const { selectedLocation } = useOutletContext() || {};

  const [activeTab, setActiveTab] = useState('responses-viewer'); // 'create', 'templates', 'responses-viewer'
  const [templates, setTemplates] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [okMessage, setOkMessage] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState(buildTemplateForm());
  const [templateStudyId, setTemplateStudyId] = useState('');
  const [templateCohortId, setTemplateCohortId] = useState('');
  const [templateStudyYearId, setTemplateStudyYearId] = useState('');

  // Filters for responses viewer
  const [filterStudy, setFilterStudy] = useState('');
  const [filterCohort, setFilterCohort] = useState('');
  const [filterStudyYear, setFilterStudyYear] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [selectedResponseTemplate, setSelectedResponseTemplate] = useState(null);

  const canManageTemplates = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'instructor';

  const instructorModuleIds = useMemo(() => {
    if (user?.role !== 'instructor') return null;
    const userId = String(user?.id || user?._id || '');
    const ids = new Set();

    (data.courses || []).forEach((course) => {
      const courseInstructorId = String(course?.instructor?.id || course?.instructor?._id || course?.instructor || '');
      if (courseInstructorId !== userId) return;

      const singleModuleId = getIdString(course.module);
      if (singleModuleId) ids.add(singleModuleId);

      (course.modules || []).forEach((moduleRef) => {
        const moduleId = getIdString(moduleRef);
        if (moduleId) ids.add(moduleId);
      });
    });

    return ids;
  }, [data.courses, user]);

  const moduleOptions = useMemo(() => {
    const items = [];
    (data.studies || []).forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        (year.modules || []).forEach((moduleDoc) => {
          const moduleId = getIdString(moduleDoc);
          if (!moduleId) return;
          items.push({
            id: moduleId,
            label: `${study.name} - ${year.name || `Jaar ${year.yearNumber || year.year || '-'}`} - ${moduleDoc.code || moduleDoc.name}`
          });
        });
      });
    });

    const deduped = new Map();
    items.forEach((item) => {
      if (!deduped.has(item.id)) deduped.set(item.id, item);
    });

    const filtered = Array.from(deduped.values()).filter((item) => {
      if (user?.role !== 'instructor') return true;
      return instructorModuleIds?.has(item.id);
    });

    return filtered.sort((left, right) => left.label.localeCompare(right.label, 'nl'));
  }, [data.studies, instructorModuleIds, user]);

  const moduleMetaById = useMemo(() => {
    const map = new Map();
    (data.studies || []).forEach((study) => {
      const studyId = getIdString(study);
      (study.studyYears || []).forEach((year) => {
        const studyYearId = getIdString(year);
        const studyYearName = year.name || `Jaar ${year.yearNumber || year.year || '-'}`;
        (year.modules || []).forEach((moduleDoc) => {
          const moduleId = getIdString(moduleDoc);
          if (!moduleId) return;
          map.set(moduleId, {
            studyId,
            studyName: study.name || '-',
            studyYearId,
            studyYearName,
            moduleId,
            moduleName: moduleDoc.name || moduleDoc.code || '-'
          });
        });
      });
    });
    return map;
  }, [data.studies]);

  // Get filter options
  const studyOptions = useMemo(() => {
    return (data.studies || []).map((study) => ({
      id: getIdString(study),
      name: study.name
    }));
  }, [data.studies]);

  const templateStudyOptions = studyOptions;

  const templateCohortOptions = useMemo(() => {
    if (!templateStudyId) return [];
    const study = (data.studies || []).find((item) => getIdString(item) === String(templateStudyId));
    return (study?.cohorts || []).map((cohort) => ({
      id: getIdString(cohort),
      name: cohort.name
    }));
  }, [data.studies, templateStudyId]);

  const templateStudyYearOptions = useMemo(() => {
    if (!templateStudyId) return [];
    const study = (data.studies || []).find((item) => getIdString(item) === String(templateStudyId));
    return (study?.studyYears || []).map((year) => ({
      id: getIdString(year),
      name: year.name || `Jaar ${year.yearNumber || year.year || '-'}`
    }));
  }, [data.studies, templateStudyId]);

  const templateModuleOptions = useMemo(() => {
    return (moduleOptions || []).filter((option) => {
      const meta = moduleMetaById.get(String(option.id));
      if (!meta) return false;
      if (templateStudyId && String(meta.studyId) !== String(templateStudyId)) return false;
      if (templateStudyYearId && String(meta.studyYearId) !== String(templateStudyYearId)) return false;
      return true;
    });
  }, [moduleMetaById, moduleOptions, templateStudyId, templateStudyYearId]);

  const cohortOptions = useMemo(() => {
    const filteredStudies = filterStudy 
      ? (data.studies || []).filter((study) => getIdString(study) === String(filterStudy))
      : (data.studies || []);
    const cohorts = [];
    filteredStudies.forEach(study => {
      (study.cohorts || []).forEach(cohort => {
        cohorts.push({ id: getIdString(cohort), name: cohort.name });
      });
    });
    return cohorts;
  }, [data.studies, filterStudy]);

  const studyYearOptions = useMemo(() => {
    const filteredStudies = filterStudy 
      ? (data.studies || []).filter((study) => getIdString(study) === String(filterStudy))
      : (data.studies || []);
    const years = [];
    filteredStudies.forEach(study => {
      (study.studyYears || []).forEach(year => {
        years.push({ id: getIdString(year), name: year.name || `Jaar ${year.yearNumber}` });
      });
    });
    return years;
  }, [data.studies, filterStudy]);

  // Filter responses by selected criteria
  const filteredResponses = useMemo(() => {
    let filtered = [...responses];
    if (selectedResponseTemplate?.id) {
      filtered = filtered.filter((response) => getIdString(response.template) === String(selectedResponseTemplate.id));
    }
    if (filterStudy) {
      filtered = filtered.filter((response) => {
        const meta = moduleMetaById.get(getIdString(response.module));
        return String(meta?.studyId || '') === String(filterStudy);
      });
    }
    if (filterCohort) filtered = filtered.filter((response) => getIdString(response.cohort) === String(filterCohort));
    if (filterStudyYear) {
      filtered = filtered.filter((response) => {
        const meta = moduleMetaById.get(getIdString(response.module));
        return String(meta?.studyYearId || '') === String(filterStudyYear);
      });
    }
    if (filterModule) filtered = filtered.filter((response) => getIdString(response.module) === String(filterModule));
    return filtered;
  }, [responses, selectedResponseTemplate, filterStudy, filterCohort, filterStudyYear, filterModule, moduleMetaById]);

  // Calculate response aggregate statistics per question
  const responseStatistics = useMemo(() => {
    if (!selectedResponseTemplate || filteredResponses.length === 0) return null;
    
    const stats = {};
    (selectedResponseTemplate.segments || []).forEach(segment => {
      stats[segment.key] = {};
      (segment.questions || []).forEach(question => {
        const questionId = getIdString(question);
        const answersForQuestion = filteredResponses
          .flatMap((response) => Array.isArray(response.answers) ? response.answers : [])
          .filter((segmentAnswers) => String(segmentAnswers.segmentKey || '') === String(segment.key))
          .flatMap((segmentAnswers) => Array.isArray(segmentAnswers.answers) ? segmentAnswers.answers : [])
          .filter((answer) => String(answer.questionId || '') === String(questionId));
        
        const counts = {
          'helemaal_oneens': 0,
          'oneens': 0,
          'eens': 0,
          'helemaal_eens': 0,
          'niet_van_toepassing': 0
        };
        
        answersForQuestion.forEach(answer => {
          if (counts.hasOwnProperty(answer.score)) {
            counts[answer.score]++;
          }
        });
        
        stats[segment.key][questionId] = {
          question: question.text,
          totalResponses: answersForQuestion.length,
          scores: counts
        };
      });
    });
    
    return stats;
  }, [selectedResponseTemplate, filteredResponses]);

  const responseExportRows = useMemo(() => {
    if (!selectedResponseTemplate || !responseStatistics) return [];

    const rows = [];
    (selectedResponseTemplate.segments || []).forEach((segment) => {
      (segment.questions || []).forEach((question) => {
        const questionId = getIdString(question);
        const stats = responseStatistics[segment.key]?.[questionId];
        if (!stats) return;

        const totalResponses = Number(stats.totalResponses || 0);
        SCORE_OPTIONS.forEach((scoreOption) => {
          const count = Number(stats.scores?.[scoreOption.value] || 0);
          const percentage = totalResponses > 0 ? ((count / totalResponses) * 100).toFixed(1) : '0.0';
          rows.push({
            Template: selectedResponseTemplate.name,
            Segment: segment.title,
            Vraag: stats.question,
            Antwoord: scoreOption.label,
            Aantal: count,
            Percentage: `${percentage}%`,
            TotaalAntwoorden: totalResponses
          });
        });
      });
    });

    return rows;
  }, [selectedResponseTemplate, responseStatistics]);

  const exportResponsesPdf = () => {
    if (!responseExportRows.length) {
      window.alert('Geen gegevens beschikbaar voor export.');
      return;
    }

    const fileStamp = new Date().toISOString().slice(0, 10);
    exportToPDF(
      responseExportRows,
      `module-evaluaties-${fileStamp}`,
      `Module Evaluaties - ${selectedResponseTemplate?.name || ''}`,
      [
        { header: 'Segment', dataKey: 'Segment' },
        { header: 'Vraag', dataKey: 'Vraag' },
        { header: 'Antwoord', dataKey: 'Antwoord' },
        { header: 'Aantal', dataKey: 'Aantal' },
        { header: 'Percentage', dataKey: 'Percentage' },
        { header: 'Totaal', dataKey: 'TotaalAntwoorden' }
      ]
    );
  };

  const exportResponsesExcel = () => {
    if (!responseExportRows.length) {
      window.alert('Geen gegevens beschikbaar voor export.');
      return;
    }

    const fileStamp = new Date().toISOString().slice(0, 10);
    exportToExcel(
      responseExportRows,
      `module-evaluaties-${fileStamp}`,
      'Evaluatie Analyse',
      `Module Evaluaties - ${selectedResponseTemplate?.name || ''}`
    );
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const locationFilter = selectedLocation && selectedLocation !== 'all'
        ? (selectedLocation.charAt(0).toUpperCase() + selectedLocation.slice(1))
        : undefined;

      const [templatesResp, responsesResp] = await Promise.all([
        moduleEvaluationsAPI.getTemplates({ includeInactive: 'true' }),
        moduleEvaluationsAPI.getResponses(locationFilter ? { location: locationFilter } : {})
      ]);

      setTemplates(templatesResp.templates || []);
      setResponses(responsesResp.responses || []);
    } catch (loadError) {
      setError(loadError.message || 'Laden van evaluaties mislukt');
    } finally {
      setLoading(false);
    }
  }, [selectedLocation]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const resetForm = useCallback(() => {
    setEditingTemplate(null);
    setForm(buildTemplateForm());
    setTemplateStudyId('');
    setTemplateCohortId('');
    setTemplateStudyYearId('');
  }, []);

  const setSegmentQuestionText = (segmentIndex, questionIndex, value) => {
    setForm((prev) => {
      const next = { ...prev, segments: [...prev.segments] };
      next.segments[segmentIndex] = {
        ...next.segments[segmentIndex],
        questions: [...next.segments[segmentIndex].questions]
      };
      next.segments[segmentIndex].questions[questionIndex] = {
        ...next.segments[segmentIndex].questions[questionIndex],
        text: value
      };
      return next;
    });
  };

  const addQuestion = (segmentIndex) => {
    setForm((prev) => {
      const next = { ...prev, segments: [...prev.segments] };
      const segment = next.segments[segmentIndex];
      next.segments[segmentIndex] = {
        ...segment,
        questions: [
          ...(segment.questions || []),
          {
            id: `${segment.key}-${Date.now()}`,
            text: '',
            sortOrder: (segment.questions || []).length,
            isActive: true
          }
        ]
      };
      return next;
    });
  };

  const removeQuestion = (segmentIndex, questionIndex) => {
    setForm((prev) => {
      const next = { ...prev, segments: [...prev.segments] };
      const segment = next.segments[segmentIndex];
      next.segments[segmentIndex] = {
        ...segment,
        questions: (segment.questions || []).filter((_, idx) => idx !== questionIndex)
      };
      return next;
    });
  };

  const saveTemplate = async (event) => {
    event.preventDefault();
    if (!canManageTemplates) return;

    setSaving(true);
    setError('');
    setOkMessage('');
    try {
      const payload = {
        name: form.name,
        description: form.description,
        moduleId: form.moduleId,
        location: form.location,
        isActive: form.isActive,
        segments: (form.segments || []).map((segment, segmentIndex) => ({
          key: segment.key,
          title: segment.title,
          sortOrder: Number(segment.sortOrder || segmentIndex + 1),
          questions: (segment.questions || [])
            .map((question, questionIndex) => {
              const isExistingId = /^[0-9a-f]{24}$/i.test(String(question.id || ''));
              return {
                ...(isExistingId ? { _id: question.id } : {}),
                text: String(question.text || '').trim(),
                sortOrder: Number(question.sortOrder || questionIndex),
                isActive: question.isActive !== false
              };
            })
            .filter((question) => question.text)
        }))
      };

      if (!payload.name || !payload.moduleId) {
        setError('Naam en module zijn verplicht');
        return;
      }

      if (editingTemplate?.id) {
        await moduleEvaluationsAPI.updateTemplate(editingTemplate.id, payload);
        setOkMessage('Template bijgewerkt');
      } else {
        await moduleEvaluationsAPI.createTemplate(payload);
        setOkMessage('Template aangemaakt');
      }

      resetForm();
      await loadAll();
    } catch (saveError) {
      setError(saveError.message || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const editTemplate = (template) => {
    setEditingTemplate(template);
    setForm(buildTemplateForm(template));
    const moduleId = getIdString(template?.module);
    const moduleMeta = moduleMetaById.get(moduleId);
    setTemplateStudyId(moduleMeta?.studyId || '');
    setTemplateCohortId('');
    setTemplateStudyYearId(moduleMeta?.studyYearId || '');
  };

  const deactivateTemplate = async (template) => {
    if (!canManageTemplates) return;
    if (!window.confirm(`Template "${template.name}" deactiveren?`)) return;

    try {
      setError('');
      setOkMessage('');
      await moduleEvaluationsAPI.deleteTemplate(template.id);
      setOkMessage('Template gedeactiveerd');
      await loadAll();
    } catch (deactivateError) {
      setError(deactivateError.message || 'Deactiveren mislukt');
    }
  };

  return (
    <div className="module-evaluations-page">
      <div className="page-header">
        <div>
          <h1>Module Evaluaties</h1>
          <p className="page-subtitle">Beheer segmenten en vragen per module, en bekijk ingezonden evaluaties.</p>
        </div>
      </div>

      {/* Tab Navigation Menu */}
      <div className="me-nav-menu">
        <button 
          className={`me-nav-item ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          Templates Aanmaken
        </button>
        <button 
          className={`me-nav-item ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Bestaande Templates
        </button>
        <button 
          className={`me-nav-item ${activeTab === 'responses-viewer' ? 'active' : ''}`}
          onClick={() => setActiveTab('responses-viewer')}
        >
          Ingezonden Evaluaties
        </button>
      </div>

      {error && <p className="message error">{error}</p>}
      {okMessage && <p className="message ok">{okMessage}</p>}

      {/* Create Template Tab */}
      {activeTab === 'create' && canManageTemplates && (
        <form className="me-template-form" onSubmit={saveTemplate}>
          <h2>{editingTemplate ? 'Template bewerken' : 'Nieuw evaluatie-template'}</h2>

          <div className="me-grid two-cols">
            <div>
              <label>Naam</label>
              <input
                className="form-input"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Bijv. Evaluatie Jaar 1 - Module Marketing"
                required
              />
            </div>
            <div>
              <label>Module</label>
              <select
                className="form-input"
                value={form.moduleId}
                onChange={(e) => setForm((prev) => ({ ...prev, moduleId: e.target.value }))}
                required
              >
                <option value="">-- Kies module --</option>
                {moduleOptions.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="me-grid two-cols">
            <div>
              <label>Studie</label>
              <select
                className="form-input"
                value={templateStudyId}
                onChange={(e) => {
                  setTemplateStudyId(e.target.value);
                  setTemplateCohortId('');
                  setTemplateStudyYearId('');
                  setForm((prev) => ({ ...prev, moduleId: '' }));
                }}
              >
                <option value="">-- Kies studie --</option>
                {templateStudyOptions.map((option) => (
                  <option key={option.id} value={option.id}>{option.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Cohort</label>
              <select
                className="form-input"
                value={templateCohortId}
                onChange={(e) => setTemplateCohortId(e.target.value)}
                disabled={!templateStudyId}
              >
                <option value="">-- Kies cohort (optioneel) --</option>
                {templateCohortOptions.map((option) => (
                  <option key={option.id} value={option.id}>{option.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="me-grid two-cols">
            <div>
              <label>Studiejaar</label>
              <select
                className="form-input"
                value={templateStudyYearId}
                onChange={(e) => {
                  setTemplateStudyYearId(e.target.value);
                  setForm((prev) => ({ ...prev, moduleId: '' }));
                }}
                disabled={!templateStudyId}
              >
                <option value="">-- Kies studiejaar --</option>
                {templateStudyYearOptions.map((option) => (
                  <option key={option.id} value={option.id}>{option.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Module</label>
              <select
                className="form-input"
                value={form.moduleId}
                onChange={(e) => {
                  const nextModuleId = e.target.value;
                  const nextMeta = moduleMetaById.get(String(nextModuleId));
                  setForm((prev) => ({ ...prev, moduleId: nextModuleId }));
                  if (nextMeta?.studyId) setTemplateStudyId(nextMeta.studyId);
                  if (nextMeta?.studyYearId) setTemplateStudyYearId(nextMeta.studyYearId);
                }}
                required
              >
                <option value="">-- Kies module --</option>
                {templateModuleOptions.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
              {templateStudyId && templateModuleOptions.length === 0 && (
                <p className="hint small">Geen vakken gevonden voor de gekozen studie/studiejaar combinatie.</p>
              )}
            </div>
          </div>

          <div className="me-grid two-cols">
            <div>
              <label>Locatie</label>
              <select
                className="form-input"
                value={form.location}
                onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
              >
                <option value="all">Alle locaties</option>
                <option value="Paramaribo">Paramaribo</option>
                <option value="Nickerie">Nickerie</option>
              </select>
            </div>
            <div>
              <label>Status</label>
              <select
                className="form-input"
                value={String(form.isActive)}
                onChange={(e) => setForm((prev) => ({ ...prev, isActive: String(e.target.value) === 'true' }))}
              >
                <option value="true">Actief</option>
                <option value="false">Inactief</option>
              </select>
            </div>
          </div>

          <div>
            <label>Beschrijving</label>
            <textarea
              className="form-input"
              rows={2}
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            />
          </div>

          <div className="me-segments">
            {form.segments.map((segment, segmentIndex) => (
              <div key={segment.key} className="me-segment-card">
                <div className="me-segment-header">
                  <h3>{segment.title}</h3>
                  <button type="button" className="btn-secondary" onClick={() => addQuestion(segmentIndex)}>
                    + Vraag
                  </button>
                </div>

                {(segment.questions || []).length === 0 && (
                  <p className="hint">Nog geen vragen in dit segment.</p>
                )}

                {(segment.questions || []).map((question, questionIndex) => (
                  <div key={question.id} className="me-question-row">
                    <input
                      className="form-input"
                      value={question.text}
                      placeholder={`Vraag ${questionIndex + 1}`}
                      onChange={(e) => setSegmentQuestionText(segmentIndex, questionIndex, e.target.value)}
                    />
                    <button type="button" className="btn-delete" onClick={() => removeQuestion(segmentIndex, questionIndex)}>
                      Verwijder
                    </button>
                  </div>
                ))}

                <p className="hint small">Beschikbare scores: {SCORE_OPTIONS.map((opt) => opt.label).join(', ')}</p>
              </div>
            ))}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Opslaan...' : (editingTemplate ? 'Wijzigingen opslaan' : 'Template opslaan')}
            </button>
            <button type="button" className="btn-secondary" onClick={resetForm}>Reset</button>
          </div>
        </form>
      )}

      {/* Bestaande Templates Tab */}
      {activeTab === 'templates' && (
      <section className="me-template-list">
        <h2>Bestaande templates</h2>
        {loading ? (
          <p>Laden...</p>
        ) : templates.length === 0 ? (
          <p>Geen templates gevonden.</p>
        ) : (
          <div className="me-cards-grid">
            {templates.map((template) => (
              <article key={template.id} className="me-template-card">
                <div className="me-template-top">
                  <h3>{template.name}</h3>
                  <span className={`status-pill ${template.isActive ? 'active' : 'inactive'}`}>
                    {template.isActive ? 'Actief' : 'Inactief'}
                  </span>
                </div>
                <p className="small">Locatie: {template.location}</p>
                <p className="small">Module: {template.module?.name || template.module?.code || '-'}</p>
                <p className="small">Segmenten: {(template.segments || []).length}</p>
                <p className="small">Vragen: {(template.segments || []).reduce((sum, segment) => sum + (segment.questions || []).length, 0)}</p>
                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={() => editTemplate(template)}>Bewerken</button>
                  {canManageTemplates && (
                    <button type="button" className="btn-delete" onClick={() => deactivateTemplate(template)}>Deactiveer</button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
      )}

      {/* Ingezonden Evaluaties Tab with Graphical Viewer */}
      {activeTab === 'responses-viewer' && (
      <section className="me-responses-list">
        <h2>Ingezonden evaluaties</h2>
        
        {/* Filters */}
        <div className="me-filter-panel">
          <div className="me-filter-group">
            <label>Studie</label>
            <select value={filterStudy} onChange={(e) => { setFilterStudy(e.target.value); setFilterCohort(''); setFilterStudyYear(''); }}>
              <option value="">Alle studies</option>
              {studyOptions.map((study) => (
                <option key={study.id} value={study.id}>{study.name}</option>
              ))}
            </select>
          </div>
          <div className="me-filter-group">
            <label>Cohort</label>
            <select value={filterCohort} onChange={(e) => setFilterCohort(e.target.value)}>
              <option value="">Alle cohorten</option>
              {cohortOptions.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="me-filter-group">
            <label>Studiejaar</label>
            <select value={filterStudyYear} onChange={(e) => setFilterStudyYear(e.target.value)}>
              <option value="">Alle jaren</option>
              {studyYearOptions.map(y => (
                <option key={y.id} value={y.id}>{y.name}</option>
              ))}
            </select>
          </div>
          <div className="me-filter-group">
            <label>Module</label>
            <select value={filterModule} onChange={(e) => setFilterModule(e.target.value)}>
              <option value="">Alle modules</option>
              {moduleOptions.map(m => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="me-filter-group">
            <label>Template selecteren</label>
            <select value={selectedResponseTemplate?.id || ''} onChange={(e) => {
              const templateId = e.target.value;
              const template = templates.find(t => t.id === templateId);
              setSelectedResponseTemplate(template || null);
            }}>
              <option value="">-- Kies template --</option>
              {templates.filter(t => t.isActive).map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Response Statistics Viewer */}
        {selectedResponseTemplate && responseStatistics ? (
          <div className="me-responses-viewer">
            <div className="me-responses-info">
              <p>Totaal ingezonden antwoorden: <strong>{filteredResponses.length}</strong></p>
              <p>Template: <strong>{selectedResponseTemplate.name}</strong></p>
              <div className="me-export-actions">
                <button type="button" className="btn-secondary" onClick={exportResponsesPdf}>Export PDF</button>
                <button type="button" className="btn-secondary" onClick={exportResponsesExcel}>Export Excel</button>
              </div>
            </div>
            
            {(selectedResponseTemplate.segments || []).map((segment, segmentIdx) => (
              <div key={segment.key} className="me-segment-responses">
                <h3>{segment.title}</h3>
                
                {(segment.questions || []).map((question) => {
                  const questionId = question.id || question._id;
                  const stats = responseStatistics[segment.key]?.[questionId];
                  if (!stats) return null;
                  
                  const total = stats.totalResponses || 1;
                  return (
                    <div key={questionId} className="me-question-response">
                      <h4>{question.text || `Vraag zonder tekst`}</h4>
                      <p className="me-response-count">Antwoorden: {stats.totalResponses}</p>
                      
                      <div className="me-response-chart">
                        {['helemaal_eens', 'eens', 'oneens', 'helemaal_oneens', 'niet_van_toepassing'].map((scoreKey) => (
                          <div key={scoreKey} className="me-response-bar">
                            <div className="me-bar-label">
                              {SCORE_OPTIONS.find(s => s.value === scoreKey)?.label || scoreKey}
                            </div>
                            <div className="me-bar-container">
                              <div 
                                className={`me-bar-fill ${scoreKey}`}
                                style={{ width: `${(stats.scores[scoreKey] / total) * 100}%` }}
                              >
                                {stats.scores[scoreKey] > 0 && <span className="me-bar-value">{stats.scores[scoreKey]}</span>}
                              </div>
                            </div>
                            <div className="me-bar-percent">{((stats.scores[scoreKey] / total) * 100).toFixed(0)}%</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : (
          <p className="me-placeholder">Selecteer een template en filters om antwoorden weer te geven.</p>
        )}
      </section>
      )}
    </div>
  );
};

export default ModuleEvaluations;
